import { Wallet } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { CardShell, EmptyState } from './card-shell';

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  });
}

export function FinancialOverviewCard() {
  const summaryQ = useQuery({
    queryKey: ['financial', 'stats', 'summary'],
    queryFn: () => apiClient.financialStatsSummary(),
    staleTime: 1000 * 60,
  });

  if (summaryQ.isLoading) {
    return (
      <CardShell title="Financeiro" icon={Wallet}>
        <p className="text-xs text-[var(--fg-muted)]">Carregando...</p>
      </CardShell>
    );
  }

  const s = summaryQ.data;
  if (!s) {
    return (
      <CardShell title="Financeiro" icon={Wallet}>
        <EmptyState
          icon={Wallet}
          message="Sem lançamentos financeiros."
          cta={{ label: 'Abrir financeiro', href: '/escritorio/financeiro' }}
        />
      </CardShell>
    );
  }

  const noData =
    s.saldoAtual === 0 &&
    s.receitaMensalPrevista === 0 &&
    s.despesaMensalPrevista === 0;

  if (noData) {
    return (
      <CardShell title="Financeiro" icon={Wallet}>
        <EmptyState
          icon={Wallet}
          message="Sem lançamentos financeiros."
          cta={{ label: 'Abrir financeiro', href: '/escritorio/financeiro' }}
        />
      </CardShell>
    );
  }

  const saldoColor = s.saldoAtual < 0 ? 'text-red-600' : 'text-[var(--fg)]';
  const atrasados = s.pagamentosAtrasados;

  return (
    <CardShell title="Financeiro" icon={Wallet}>
      <Link
        to="/escritorio/financeiro"
        className="flex flex-col items-center gap-1 w-full text-center hover:opacity-80 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] rounded"
      >
        <span className="text-[10px] uppercase text-[var(--fg-muted)]">
          Saldo atual
        </span>
        <span
          className={cn(
            'tabular-nums text-2xl font-semibold',
            saldoColor,
          )}
        >
          {formatBrl(s.saldoAtual)}
        </span>
        <div className="flex items-center gap-4 text-xs mt-2">
          <span className="tabular-nums text-emerald-500">
            ↑ {formatBrl(s.receitaMensalRealizada)}
          </span>
          <span className="tabular-nums text-red-500">
            ↓ {formatBrl(s.despesaMensalRealizada)}
          </span>
        </div>
        {atrasados.count > 0 && (
          <span className="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-red-500/15 text-red-400">
            <span className="tabular-nums">{atrasados.count}</span> atrasado(s) · <span className="tabular-nums">{formatBrl(atrasados.total)}</span>
          </span>
        )}
      </Link>
    </CardShell>
  );
}
